import React, { useEffect, useState } from "react";
import {
  motion,
  useMotionValue,
  AnimatePresence,
  useTransform,
  useSpring,
} from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";

// Import icons
import {
  ArrowRight,
  Trophy,
  Star,
  Shield,
  ChevronLeft,
  Calendar,
  MapPin,
  Users,
  Award,
  Globe,
  ChevronRight,
  ChevronDown,
  Play,
} from "lucide-react";

const TiltCard = ({ img, title, desc }) => {
  // raw mouse position
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // map to rotation angles
  const rawRotateY = useTransform(mouseX, [-100, 100], [-15, 15]);
  const rawRotateX = useTransform(mouseY, [-100, 100], [15, -15]);

  // spring-smoothed rotations
  const rotateY = useSpring(rawRotateY, { stiffness: 50, damping: 20 });
  const rotateX = useSpring(rawRotateX, { stiffness: 50, damping: 20 });

  const handleMouse = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top - rect.height / 2);
  };

  return (
    <motion.div
      className="group bg-gradient-to-br from-gray-100 to-white dark:from-gray-800 dark:to-gray-900 rounded-3xl overflow-hidden cursor-pointer shadow-lg hover:bg-gray-50 dark:hover:bg-gray-800"
      style={{
        perspective: 1000,
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
      }}
      onMouseMove={handleMouse}
      onMouseLeave={() => {
        mouseX.set(0);
        mouseY.set(0);
      }}
      whileTap={{ scale: 0.97 }}
      whileHover={{ boxShadow: "0 30px 60px rgba(0,0,0,0.2)" }}
      transition={{ type: "spring", stiffness: 50, damping: 20 }}
    >
      <div className="h-48 overflow-hidden">
        <motion.img
          src={img}
          alt={title}
          className="w-full h-full object-cover"
          whileHover={{ scale: 1.08 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <div className="p-6 ">
        <motion.h3
          className="text-2xl font-semibold mb-2 text-gray-900 dark:text-gray-100 group-hover:text-amber-500 transition-colors"
          transition={{ duration: 0.3 }}
        >
          {title}
        </motion.h3>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed ">
          {desc}
        </p>
      </div>
    </motion.div>
  );
};
const AboutUs = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [activeTab, setActiveTab] = useState("skoda");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const items = [
    {
      title: "Automotive",
      img: "https://ik.imagekit.io/7mw0on9oya/car.jpg?updatedAt=1747304552675",
      desc: "Authorized dealerships for SKODA and Hyundai Motors across Himachal Pradesh.",
    },
    {
      title: "Hospitality",
      img: "https://ik.imagekit.io/jncw2kb8u/hotel-in-una1.jpg?updatedAt=1745481996268",
      desc: 'Luxurious stays at "The Gulmohar Grand" with world-class dining.',
    },
    {
      title: "Iron Trading",
      img: "https://ik.imagekit.io/jncw2kb8u/morteza-mohammadi-P8jEvckndSE-unsplash.jpg?updatedAt=1746036462692",
      desc: "Our roots in iron-rod trading, powering regional infrastructure.",
    },
    {
      title: "Management",
      img: "https://ik.imagekit.io/tdlebsr5e/WhatsApp%20Image%202025-04-28%20at%2018.49.18.jpeg",
      desc: "Consultancy services built on decades of local expertise.",
    },
  ];

  // Array of hero section content to rotate through
  const heroContent = [
    {
      label: "Automotive",
      title: "Leading the Automotive Revolution",
      description:
        "Authorized dealerships for SKODA and Hyundai with exceptional service standards",
      image:
        "https://ik.imagekit.io/6oa6qejxe/WhatsApp%20Image%202025-04-28%20at%2019.08.57_11a042da.jpg?updatedAt=1746002690933", // For placeholder - would be actual image in production
    },
    {
      label: "Hospitality",
      title: "Redefining Luxury Experiences",
      description:
        "The Gulmohar Grand and other premium hospitality ventures across the region",
      image:
        "https://ik.imagekit.io/6oa6qejxe/the-gulmohar-grand-hotel-behdala-una-himachal-pradesh-0drs7zkhmb.avif?updatedAt=1746002405350", // For placeholder - would be actual image in production
    },
    {
      label: "Trading",
      title: "Building Foundations Since 2005",
      description:
        "Our heritage business of iron rod trading that started it all",
      image:
        "https://ik.imagekit.io/6oa6qejxe/3253.jpg?updatedAt=1746001125733", // For placeholder - would be actual image in production
    },
    {
      label: "Management",
      title: "Strategic Vision for Growth",
      description:
        "Consultancy services leveraging decades of regional business expertise",
      image:
        "https://ik.imagekit.io/6oa6qejxe/2147826529.jpg?updatedAt=1746003039857", // For placeholder - would be actual image in production
    },
  ];

  // Auto-rotate through sections
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSection((prev) => (prev + 1) % heroContent.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [heroContent.length]);

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 800, // animation duration
      once: true, // only animate once
      mirror: false, // do not animate out when scrolling past
      offset: 100, // offset (in px) from the original trigger point
    });
  }, []);

  // Check screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Award data
  const awards = {
    skoda: [
      {
        icon: <Trophy className="w-12 h-12 text-amber-500" />,
        title: "3rd Position in Best Dealer Sale And Service",
        year: "2015",
        description:
          "Recognized for excellence in customer service and sales performance among all SKODA dealerships.",
      },
      {
        icon: <Award className="w-12 h-12 text-amber-500" />,
        title: "Highest Target Achievement",
        year: "2021",
        description:
          "Achieved the highest sales targets in the non-Metro category, demonstrating exceptional market performance.",
      },
      {
        icon: <Star className="w-12 h-12 text-amber-500" />,
        title: "Highest Kushaq Market Share",
        year: "2021",
        description:
          "Dominated the non-metro market with the highest market share for SKODA Kushaq sales.",
      },
    ],
    hyundaiService: [
      {
        icon: <Trophy className="w-12 h-12 text-blue-500" />,
        title: "Best Overall Service Satisfaction",
        year: "2017",
        description:
          "Recognized for delivering exceptional customer service and maintenance excellence.",
      },
      {
        icon: <Shield className="w-12 h-12 text-blue-500" />,
        title: "Highest Extended Warranty Penetration",
        year: "2018",
        description:
          "Led all dealerships in extended warranty sales, demonstrating customer trust and confidence.",
      },
      {
        icon: <Shield className="w-12 h-12 text-blue-500" />,
        title: "Highest Extended Warranty Penetration",
        year: "2019",
        description:
          "Maintained leadership position in extended warranty sales for the second consecutive year.",
      },
      {
        icon: <Award className="w-12 h-12 text-blue-500" />,
        title: "Best Low Cost Maintenance Award",
        year: "2021",
        description:
          "Recognized for providing exceptional value through cost-effective maintenance solutions.",
      },
      {
        icon: <Star className="w-12 h-12 text-blue-500" />,
        title: "Best Fairness of Charges",
        year: "2022",
        description:
          "Awarded for transparency and fairness in service pricing and customer communications.",
      },
    ],
    hyundaiSales: [
      {
        icon: <Trophy className="w-12 h-12 text-blue-500" />,
        title: "Best Institutional Sales Award",
        year: "2018",
        description:
          "Recognized for excellence in fleet and corporate sales performance.",
      },
      {
        icon: <Award className="w-12 h-12 text-blue-500" />,
        title: "Highest Santro Volume Sales Award",
        year: "2019",
        description:
          "Led all dealerships in Hyundai Santro sales, demonstrating strong market performance.",
      },
      {
        icon: <Shield className="w-12 h-12 text-blue-500" />,
        title: "Right Complaint Closure Award",
        year: "2022",
        description:
          "Recognized for exceptional customer satisfaction and efficient resolution of customer concerns.",
      },
      {
        icon: <Star className="w-12 h-12 text-blue-500" />,
        title: "Champions Trophy",
        year: "2024",
        description:
          "Four-time consecutive winner of the prestigious Champions Trophy, recognizing overall excellence.",
      },
    ],
  };

  // Get current awards based on active tab
  const getCurrentAwards = () => {
    switch (activeTab) {
      case "skoda":
        return awards.skoda;
      case "hyundaiService":
        return awards.hyundaiService;
      case "hyundaiSales":
        return awards.hyundaiSales;
      default:
        return awards.skoda;
    }
  };

  // Handles carousel navigation
  const handleNext = () => {
    const currentAwards = getCurrentAwards();
    setCurrentIndex((prevIndex) => (prevIndex + 1) % currentAwards.length);
  };

  const handlePrev = () => {
    const currentAwards = getCurrentAwards();
    setCurrentIndex(
      (prevIndex) =>
        (prevIndex - 1 + currentAwards.length) % currentAwards.length
    );
  };

  // Get display awards
  const getDisplayAwards = () => {
    const currentAwards = getCurrentAwards();

    if (isMobile) {
      return [currentAwards[currentIndex]];
    } else {
      // For desktop, show 3 awards at a time
      const displayCount = Math.min(3, currentAwards.length);
      const startIndex = currentIndex % currentAwards.length;

      const result = [];
      for (let i = 0; i < displayCount; i++) {
        const index = (startIndex + i) % currentAwards.length;
        result.push(currentAwards[index]);
      }

      return result;
    }
  };

  const displayAwards = getDisplayAwards();
  const currentAwards = getCurrentAwards();
  const brandColor =
    activeTab === "skoda"
      ? "from-green-600 to-green-700"
      : "from-blue-600 to-blue-700";

  return (
    <div className="font-sans bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Enhanced Premium Hero Section */}
      <section className="relative overflow-hidden bg-black dark:bg-black h-[820px]">
        {/* Hero Background - Animated Slider */}
        <div className="absolute inset-0 z-0">
          {heroContent.map((content, index) => (
            <motion.div
              key={`slide-${index}`}
              initial={{ opacity: 0 }}
              animate={{
                opacity: index === currentSection ? 1 : 0,
                scale: index === currentSection ? 1 : 1.1,
              }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent z-10"></div>
              <img
                src={content.image}
                alt={content.label}
                className="w-full h-full object-cover"
              />
            </motion.div>
          ))}
        </div>

        {/* Angled Overlay */}
        <div className="absolute inset-0 z-10">
          <svg
            className="h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <polygon points="0,0 60,0 40,100 0,100" className="fill-black/50" />
          </svg>
        </div>

        <div className="container mx-auto px-4 h-full relative z-20 flex flex-col justify-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full items-center">
            {/* Left Content Area */}
            <div className="pt-20 lg:pt-0">
              {/* Company Logo/Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-6"
              >
                <div className="inline-flex items-center gap-2 bg-amber-500/10 backdrop-blur-sm rounded-full px-4 py-2 border border-amber-500/30">
                  <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                    <span className="font-bold text-black dark:text-black">
                      DB
                    </span>
                  </div>
                  <span className="text-amber-400 font-semibold">
                    Dev Bhoomi Group Companies
                  </span>
                </div>
              </motion.div>

              {/* Dynamic Section Label */}
              <motion.div
                key={`label-${currentSection}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-3"
              >
                <span className="text-amber-500 font-medium tracking-wider">
                  {heroContent[currentSection].label}
                </span>
              </motion.div>

              {/* Dynamic Headline */}
              <motion.h1
                key={`title-${currentSection}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="text-5xl xl:text-6xl font-bold text-white dark:text-white mb-6 leading-tight"
              >
                {heroContent[currentSection].title}
              </motion.h1>

              {/* Animated Underline */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="h-1 w-24 bg-amber-500 mb-6 origin-left"
              />

              {/* Dynamic Description */}
              <motion.p
                key={`desc-${currentSection}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="text-lg text-gray-300 dark:text-gray-300 mb-10 max-w-xl"
              >
                {heroContent[currentSection].description}
              </motion.p>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="flex flex-wrap gap-4"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    const section =
                      document.getElementById("business-verticals");
                    if (section) {
                      section.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className="px-8 py-4 bg-amber-500 dark:bg-amber-500 rounded-lg text-white dark:text-white font-medium flex items-center gap-2 hover:bg-amber-600 dark:hover:bg-amber-600 transition-all"
                >
                  Explore Business
                  <ChevronRight size={18} />
                </motion.button>
              </motion.div>

              {/* Stats Row */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
                className="grid grid-cols-3 gap-6 mt-12 max-w-lg border-t border-white/10 pt-8"
              >
                <div>
                  <div className="text-3xl font-bold text-white dark:text-white">
                    7
                  </div>
                  <div className="text-sm text-gray-400 dark:text-gray-400">
                    Companies
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white dark:text-white">
                    4
                  </div>
                  <div className="text-sm text-gray-400 dark:text-gray-400">
                    Industries
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-white dark:text-white">
                    2005
                  </div>
                  <div className="text-sm text-gray-400 dark:text-gray-400">
                    Established
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Section - Navigation Dots */}
            <div className="hidden lg:flex justify-end items-center h-full pr-8">
              <div className="flex flex-col gap-4">
                {heroContent.map((content, index) => (
                  <button
                    key={`nav-${index}`}
                    onClick={() => setCurrentSection(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentSection === index
                        ? "bg-amber-500 w-12"
                        : "bg-white/30 hover:bg-white/50"
                    }`}
                    aria-label={`View ${content.label} section`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div data-aos="fade-right">
              <h2 className="text-3xl font-bold mb-3 text-gray-900 dark:text-white">
                About Dev Bhoomi Group Companies
              </h2>
              <div className="w-20 h-1 bg-amber-500 mb-6"></div>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                Founded by Mr. Rajinder Vashista in 2005, the Dev Bhoomi Group
                Companies is headquartered in Himachal Pradesh and consists of
                seven companies across four verticals.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                The entrepreneurial journey began with iron rod trading and has
                since expanded into automobiles with authorized dealerships of
                prestigious brands like SKODA and Hyundai Motors across Himachal
                Pradesh.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-10">
                <div className="flex items-start">
                  <div className="mr-4 mt-1">
                    <Calendar className="w-10 h-10 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-gray-900 dark:text-white">
                      Established
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">2005</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="mr-4 mt-1">
                    <MapPin className="w-10 h-10 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-gray-900 dark:text-white">
                      Headquarters
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Himachal Pradesh
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="mr-4 mt-1">
                    <Users className="w-10 h-10 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-gray-900 dark:text-white">
                      Companies
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Seven and Growing
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="mr-4 mt-1">
                    <Award className="w-10 h-10 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-gray-900 dark:text-white">
                      Verticals
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Four Industries
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div data-aos="fade-right" className="relative">
              <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg shadow-lg w-full h-96 relative z-10">
                <img
                  src="https://ik.imagekit.io/jncw2kb8u/hotel-in-una1.jpg?updatedAt=1745481996268"
                  alt="Dev Bhoomi Headquarters"
                  className="w-full h-full object-cover rounded-md"
                />
              </div>
              <div className="absolute bg-amber-500 w-full h-full rounded-lg -bottom-6 -right-6 z-0"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-28 relative overflow-hidden bg-gradient-to-b from-gray-50 via-amber-50 to-gray-100 dark:from-gray-900 dark:via-gray-850 dark:to-gray-800">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full bg-amber-400"></div>
          <div className="absolute top-40 right-10 w-32 h-32 rounded-full bg-blue-500"></div>
          <div className="absolute bottom-20 left-1/4 w-48 h-48 rounded-full bg-green-400"></div>
          <div className="absolute -bottom-10 right-1/3 w-56 h-56 rounded-full bg-amber-500"></div>
        </div>

        <div className="container mx-auto px-6 lg:px-16 relative z-10">
          <div
            className="text-center mb-20"
            data-aos="fade-up"
            data-aos-duration="800"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-300 text-sm font-semibold mb-4">
              Leadership That Inspires
            </span>
            <h2 className="text-5xl font-bold mb-4 text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-amber-500 dark:from-amber-400 dark:to-amber-300">
              Visionary Leadership
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mb-8"></div>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              The driving force behind Dev Bhoomi Group Companies remarkable
              journey from humble beginnings to a multi-industry conglomerate
              transforming Himachal Pradesh.
            </p>
          </div>

          <div className="relative max-w-7xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden transform transition-all hover:shadow-amber-100 dark:hover:shadow-amber-900">
              <div className="flex flex-col">
                {/* Image Section - Now above content */}
                <div className="flex justify-center items-center w-full">
                  <div
                    className="relative w-96"
                    data-aos="fade-down"
                    data-aos-duration="1000"
                  >
                    <div className="overflow-hidden h-96 relative">
                      <img
                        src="https://ik.imagekit.io/zfayt6inj/06A0803B-521A-4652-8304-32684BA0BCB5_4_5005_c.jpeg?updatedAt=1747409890168"
                        alt="Mr. Rajinder Vashista - Founder & Chairman of Dev Bhoomi Group Companies Group"
                        className="w-full h-full object-cover object-center transition-transform duration-700 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-40"></div>
                      <div className="absolute bottom-0 left-0 right-0 text-center p-4 bg-gradient-to-t from-black to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-1">
                          Mr. Rajinder Vashista
                        </h3>
                        <p className="text-amber-300 font-medium">
                          Founder & Chairman
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content Section - Now below image */}
                <div
                  className="p-8 lg:p-12"
                  data-aos="fade-up"
                  data-aos-duration="1000"
                >
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-1 bg-amber-500 mr-4"></div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                      The Visionary Behind Dev Bhoomi Group Companies
                    </h2>
                  </div>

                  <div className="space-y-6 text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                    <p>
                      From humble beginnings as an iron and steel trader,{" "}
                      <span className="font-semibold text-amber-600 dark:text-amber-400">
                        Rajinder Vashista's
                      </span>{" "}
                      entrepreneurial vision led him to transform the business
                      landscape of Himachal Pradesh. In 2011, he ventured into
                      the automobile industry by establishing the region's first
                      ŠKODA dealership in Mandi, followed by a second successful
                      dealership in Solan in 2021, marking the beginning of an
                      extraordinary business expansion.
                    </p>
                    <p>
                      A defining moment came in 2014 when the Group secured the
                      prestigious authorized dealership of{" "}
                      <span className="font-semibold">
                        Hyundai Motors India Ltd
                      </span>
                      . Under the brand "Dev Bhoomi Hyundai," Mr. Vashista
                      orchestrated a strategic expansion across the state,
                      establishing modern showrooms and state-of-the-art service
                      workshops in Mandi, Joginder Nagar, Ghumarwin, Bilaspur,
                      Shimla, Karsog, Rampur, and Rohru—with ambitious plans for
                      further growth already underway.
                    </p>
                    <p className="relative pl-6 border-l-4 border-amber-500 italic">
                      <span className="absolute -left-3 top-0 text-5xl text-amber-400 opacity-50">
                        "
                      </span>
                      Through his customer-centric leadership philosophy, Mr.
                      Vashista has created an enterprise spanning over 500,000
                      square feet, equipped with cutting-edge infrastructure and
                      technology-driven service centers.
                    </p>
                    <p>
                      His contributions have helped Himachal Pradesh achieve the
                      remarkable distinction of having the second-highest per
                      capita income in India—an extraordinary accomplishment for
                      a predominantly rural state. As a true visionary, Rajinder
                      Vashista continues to pioneer innovations and
                      opportunities, dedicated to creating a prosperous and
                      opportunity-rich Himachal Pradesh for generations to come.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative corner elements */}
            <div className="absolute top-0 left-0 w-20 h-20 bg-amber-500 opacity-80 rounded-br-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 bg-blue-500 opacity-80 rounded-tl-3xl transform translate-x-1/2 translate-y-1/2"></div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 overflow-hidden relative">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-300 rounded-full opacity-10 transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400 rounded-full opacity-10 transform -translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="container mx-auto px-6 lg:px-16 relative z-10">
          {/* Heading */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-300 text-sm font-semibold mb-4">
              Recognition of Excellence
            </span>
            <h2 className="text-4xl font-bold mb-3 text-gray-900 dark:text-white">
              Our <span className="text-amber-500">Awards</span> & Recognitions
            </h2>
            <div className="w-20 h-1 bg-amber-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Dev Bhoomi Group's journey of excellence has been recognized by
              industry leaders through these prestigious awards
            </p>
          </motion.div>

          {/* Tabs */}
          <div className="flex flex-wrap justify-center mb-10 max-w-3xl mx-auto">
            <button
              className={`flex items-center px-6 py-3 mb-2 mx-2 rounded-full font-medium transition-all ${
                activeTab === "skoda"
                  ? "bg-gradient-to-r from-green-600 to-green-700 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
              onClick={() => {
                setActiveTab("skoda");
                setCurrentIndex(0);
              }}
            >
              <span className="mr-2">ŠKODA</span>
            </button>
            <button
              className={`flex items-center px-6 py-3 mb-2 mx-2 rounded-full font-medium transition-all ${
                activeTab === "hyundaiService"
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
              onClick={() => {
                setActiveTab("hyundaiService");
                setCurrentIndex(0);
              }}
            >
              <span className="mr-2">Hyundai Service</span>
            </button>
            <button
              className={`flex items-center px-6 py-3 mb-2 mx-2 rounded-full font-medium transition-all ${
                activeTab === "hyundaiSales"
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
              onClick={() => {
                setActiveTab("hyundaiSales");
                setCurrentIndex(0);
              }}
            >
              <span className="mr-2">Hyundai Sales</span>
            </button>
          </div>

          {/* Award cards with carousel */}
          <div className="relative max-w-6xl mx-auto">
            {/* Navigation buttons */}
            {currentAwards.length > (isMobile ? 1 : 3) && (
              <>
                <button
                  onClick={handlePrev}
                  className="absolute left-0 z-20 top-1/2 transform -translate-y-1/2 -translate-x-4 lg:-translate-x-8 bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-label="Previous award"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-0 z-20 top-1/2 transform -translate-y-1/2 translate-x-4 lg:translate-x-8 bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-label="Next award"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700 dark:text-gray-300" />
                </button>
              </>
            )}

            {/* Award cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <AnimatePresence mode="wait">
                {displayAwards.map((award, index) => (
                  <motion.div
                    key={`${activeTab}-${index}-${award.title}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden transform transition-all hover:shadow-2xl hover:-translate-y-1"
                  >
                    <div
                      className={`h-2 w-full bg-gradient-to-r ${brandColor}`}
                    ></div>
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg mr-4">
                          {award.icon}
                        </div>
                        <div>
                          <span className="inline-block px-3 py-1 text-xs rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 mb-1">
                            {award.year}
                          </span>
                          <h3 className="font-bold text-gray-900 dark:text-white text-lg">
                            {award.title}
                          </h3>
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {award.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Pagination indicators */}
            {currentAwards.length > (isMobile ? 1 : 3) && (
              <div className="flex justify-center mt-8 space-x-2">
                {Array.from({
                  length: isMobile
                    ? currentAwards.length
                    : Math.ceil(currentAwards.length / 3),
                }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i * (isMobile ? 1 : 3))}
                    className={`w-3 h-3 rounded-full transition-all ${
                      Math.floor(currentIndex / (isMobile ? 1 : 3)) === i
                        ? `bg-${
                            activeTab === "skoda" ? "green" : "blue"
                          }-500 w-6`
                        : "bg-gray-300 dark:bg-gray-600"
                    }`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Business Verticals */}
      <section id="business-verticals" data-aos="fade-up">
        <motion.section
          className="py-20 bg-gray-50 dark:bg-gray-800"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div
            className="container mx-auto px-6 lg:px-16"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.15 } },
            }}
          >
            {/* Heading */}
            <motion.div
              className="text-center mb-16"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.6, ease: "easeOut" },
                },
              }}
            >
              <h2 className="text-3xl font-bold mb-3 text-gray-900 dark:text-white">
                Our Business Verticals
              </h2>
              <div className="w-20 h-1 bg-amber-500 mx-auto mb-6" />
              <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                Dev Bhoomi Group Companies has strategically diversified across
                multiple industries to create sustainable growth and value.
              </p>
            </motion.div>

            {/* Tilt Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {items.map((item, i) => (
                <TiltCard key={i} {...item} />
              ))}
            </div>
          </motion.div>
        </motion.section>
      </section>

      {/* Growth Timeline */}
      <section className="py-20 bg-gray-900 dark:bg-gray-900 text-white">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl font-bold mb-3">Our Growth Journey</h2>
            <div className="w-20 h-1 bg-amber-500 mx-auto mb-6"></div>
            <p className="text-lg text-gray-300 dark:text-gray-300 max-w-3xl mx-auto">
              From humble beginnings to a diversified conglomerate, our journey
              of excellence and expansion.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-amber-500"></div>

            {/* Timeline items */}
            <div className="relative z-10 space-y-16">
              {/* Item Template */}
              {[
                {
                  year: "2005",
                  title: "Foundation",
                  description:
                    "Rajinder Vashista established Dev Bhoomi Group, beginning with iron rod trading operations in Himachal Pradesh.",
                },
                {
                  year: "2011",
                  title: "Automotive Expansion",
                  description:
                    "Entered the automobile industry with a SKODA authorized dealership in Mandi, Himachal Pradesh.",
                },
                {
                  year: "2014",
                  title: "Hyundai Partnership",
                  description:
                    "Became the authorized dealer of Hyundai Motors India Ltd, marking a significant expansion in the automotive sector.",
                },
                {
                  year: "2019",
                  title: "Hospitality Sector",
                  description:
                    "In 2019, the business entered the hospitality industry with the launch of The Gulmohar Grand, a premier hotel aimed at providing high-quality accommodation and services to both business and leisure travelers.",
                },
                {
                  year: "2019",
                  title: "Entertainment Sector",
                  description:
                    "The group ventured into the entertainment industry by launching Grand Cinemaz — a modern cinema experience offering the latest movies, premium comfort, and state-of-the-art screening facilities to audiences.",
                },
                {
                  year: "2020",
                  title: "Standing Together During the Pandemic",
                  description:
                    "Amid the global COVID-19 crisis, we stood firmly with our team. Every employee received full salary without a single deduction, even while safely at home — upholding our values of trust, care, and unity.",
                },
                {
                  year: "2021",
                  title: "SKODA Expansion",
                  description:
                    "Opened a new SKODA dealership in Solan, strengthening the automotive business vertical.",
                },
                {
                  year: "Present",
                  title: "Venturing into Clean Energy",
                  description:
                    "In 2025, the business ventured into the renewable energy sector with the launch of FDL Solar, offering sustainable solar energy solutions for homes, businesses, and industries.",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`flex flex-col lg:flex-row ${
                    index % 2 !== 0 ? "lg:flex-row-reverse" : ""
                  } items-center justify-between w-full`}
                  data-aos="fade-up"
                >
                  <div className="w-full lg:w-5/12"></div>

                  {/* Circle number */}
                  <div className="z-20 flex items-center justify-center bg-amber-500 shadow-xl w-10 h-10 rounded-full">
                    <h1 className="font-semibold text-lg text-black">
                      {index + 1}
                    </h1>
                  </div>

                  {/* Content Box */}
                  <div className="mt-4 lg:mt-0 bg-gray-800 rounded-lg shadow-xl w-full lg:w-5/12 px-6 py-6">
                    <h3 className="font-bold text-amber-500 text-xl mb-2">
                      {item.year}
                    </h3>
                    <h4 className="font-bold text-white text-lg mb-2">
                      {item.title}
                    </h4>
                    <p className="text-sm leading-relaxed text-gray-300">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Mission */}
            <div
              className="bg-gray-50 dark:bg-gray-800 p-10 rounded-lg shadow-lg"
              data-aos="fade-right"
            >
              <div className="mb-6 inline-block p-4 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Globe className="w-10 h-10 text-amber-600 dark:text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                Our Mission
              </h3>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                To deliver the most commendable and satisfying experience to our
                customers while providing wealth and prosperity to Himachal
                Pradesh through ethical business practices and sustainable
                growth.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="mr-2 mt-1 text-amber-500">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    Customer satisfaction is our highest priority
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-1 text-amber-500">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    Regional economic development and job creation
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-1 text-amber-500">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300">
                    Excellence in all business operations and services
                  </p>
                </li>
              </ul>
            </div>

            {/* Vision */}
            <div
              className="bg-gray-900 dark:bg-gray-800 text-white p-10 rounded-lg shadow-lg"
              data-aos="fade-right"
            >
              <div className="mb-6 inline-block p-4 bg-amber-500/20 rounded-lg">
                <Award className="w-10 h-10 text-amber-500" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
              <p className="text-lg text-gray-300 dark:text-gray-300 mb-6 leading-relaxed">
                To be the premier diversified business conglomerate in Himachal
                Pradesh, leading innovation and excellence across industries
                while contributing significantly to the region's economic and
                social development.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="mr-2 mt-1 text-amber-500">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <p className="text-gray-300 dark:text-gray-300">
                    Continuous expansion across business verticals
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-1 text-amber-500">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <p className="text-gray-300 dark:text-gray-300">
                    Setting industry benchmarks for quality and service
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="mr-2 mt-1 text-amber-500">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </div>
                  <p className="text-gray-300 dark:text-gray-300">
                    Being a catalyst for regional prosperity and development
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
